import { z } from 'zod';

import type { TaskLabelView } from '@/lib/api/generated/model';

/** The reserved member that means "carries no labels" in an address. */
const UNLABELLED = 'none';

/**
 * What a surface is narrowed to. Absent means everything.
 *
 * Two variants rather than one list with a magic member: `none` and a label
 * identifier can never hold together, and making that impossible in the type is
 * what lets the picker enforce the exclusion once instead of every consumer
 * guarding for it.
 */
export type LabelNarrowing =
  | { kind: 'labels'; ids: readonly string[] }
  | { kind: 'unlabelled' }
  | undefined;

/**
 * Whether a task survives the narrowing.
 *
 * Several labels combine with **AND**: each one added narrows. Adding a filter
 * must never widen the result — "I added a filter and more things appeared" is
 * the confusion OR produces in a flat vocabulary where a project and a priority
 * sit in the same list. OR becomes the right default only once labels are
 * grouped, which the API defers.
 */
export function matchesLabelNarrowing(
  // Only what the rule reads, so anything carrying labels can be asked — a
  // `TaskView` from the cache, or a derived row on a surface that flattened
  // several boards into one list. Taking `TaskView` forced such a caller to
  // either fabricate the rest of a task or copy its label array on every row of
  // every render, and a virtualised list does that a great many times.
  task: { labels?: readonly TaskLabelView[] | null },
  narrowing: LabelNarrowing,
): boolean {
  if (!narrowing) return true;

  // `labels` is optional in the generated model, so a task from an older cache
  // entry can arrive without it. Absent and empty mean the same thing here.
  const carried = task.labels ?? [];

  if (narrowing.kind === 'unlabelled') return carried.length === 0;
  if (narrowing.ids.length === 0) return true;

  const ids = new Set(carried.map((label) => label.id));
  return narrowing.ids.every((id) => ids.has(id));
}

/**
 * Read a narrowing out of a search parameter.
 *
 * Identifiers rather than names, because a shared link must survive someone
 * fixing a typo in a label. Unrecognised or blank members are dropped rather
 * than rejected: search parameters are user-editable text, and the address
 * should render something sensible rather than fail the route.
 */
export function parseLabelNarrowing(raw: string | undefined): LabelNarrowing {
  if (!raw?.trim()) return undefined;

  const members = raw
    .split(',')
    .map((member) => member.trim())
    .filter(Boolean);

  // The sentinel wins over any named label it was combined with. The picker
  // prevents the combination, but a hand-edited address can still carry it, and
  // "unlabelled AND acme" can never match — showing an empty board with no
  // explanation is the worse of the two readings.
  if (members.includes(UNLABELLED)) return { kind: 'unlabelled' };
  return members.length > 0 ? { kind: 'labels', ids: members } : undefined;
}

/**
 * Write a narrowing back into a search parameter, or `undefined` for none —
 * an unfiltered address says so by carrying nothing, not by carrying "all".
 */
export function serializeLabelNarrowing(
  narrowing: LabelNarrowing,
): string | undefined {
  if (!narrowing) return undefined;
  if (narrowing.kind === 'unlabelled') return UNLABELLED;
  return narrowing.ids.length > 0 ? narrowing.ids.join(',') : undefined;
}

/**
 * Drop identifiers the workspace's vocabulary no longer holds.
 *
 * A shared link outlives the labels it names. FR-025 says such an address must
 * render "with that part ignored", and the spec's clarification says an
 * unrecognised identifier is "dropped" — so an id nobody recognises contributes
 * nothing rather than narrowing the view to nothing.
 *
 * Keeping it instead is the trap this exists to close: the board would go empty
 * with no pill to clear and no clause in the subtitle, so nothing on screen
 * would say a filter was on at all. Parsing still keeps unknown ids — it has no
 * registry to check them against — which is why the drop happens here, once the
 * vocabulary is in hand, and why the address itself is left untouched.
 *
 * Returns `undefined` when nothing recognisable is left, because that is what
 * every consumer already reads as "unfiltered". The unlabelled variant needs no
 * registry and passes through unchanged.
 */
export function resolveLabelNarrowing(
  narrowing: LabelNarrowing,
  registry: readonly { id: string }[],
): LabelNarrowing {
  if (!narrowing || narrowing.kind === 'unlabelled') return narrowing;

  const known = new Set(registry.map((label) => label.id));
  const ids = narrowing.ids.filter((id) => known.has(id));
  return ids.length > 0 ? { kind: 'labels', ids } : undefined;
}

/**
 * The search-parameter field a surface adds to its route schema to become
 * narrowable, and the reader that turns a parsed search object back into a
 * narrowing.
 *
 * Both the board and the Overview carry the same parameter, and each had its own
 * copy of this field plus a one-line wrapper around `parseLabelNarrowing`. That
 * made "the encoding lives in one place" a claim held up by comment rather than
 * by structure — two identical `z.string().optional().catch(undefined)` lines
 * are exactly how a third surface ends up with a subtly different one.
 */
export const labelSearchField = z.string().optional().catch(undefined);

export function labelNarrowingOf(search: {
  labels?: string | undefined;
}): LabelNarrowing {
  return parseLabelNarrowing(search.labels);
}
